import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Film, Heart, Sparkles, Zap, Camera } from "lucide-react";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { FormatShowcase } from "@/components/site/FormatShowcase";
import { Pillars } from "@/components/site/Pillars";
import { ContactForm } from "@/components/site/ContactForm";
import { TrustBand } from "@/components/site/TrustBand";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { LivePulseBadge } from "@/components/site/wow/LivePulseBadge";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { TiltCard } from "@/components/site/wow/TiltCard";
import { ServiceMatcher } from "@/components/site/wow/ServiceMatcher";
import { CompareTable } from "@/components/site/wow/CompareTable";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";

const SITE_ORIGIN = "https://fenny.se";
const TJANSTER_URL = `${SITE_ORIGIN}/tjanster`;
import { SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import { aeoGeo } from "@/lib/seo/schema";
const TJANSTER_OG_IMAGE = SOCIAL_IMAGE_URL;

const tjansterBreadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Hem", item: `${SITE_ORIGIN}/` },
    { "@type": "ListItem", position: 2, name: "Tjänster", item: TJANSTER_URL },
  ],
};

const tjansterItemListJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Fenny — tjänster",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      item: {
        "@type": "Service",
        name: "Fenny Pulse",
        url: `${SITE_ORIGIN}/pulse`,
        serviceType: "Filmabonnemang",
        description: "Löpande filmleveranser med fast månadskostnad.",
        offers: { "@type": "Offer", priceCurrency: "SEK", price: "14995" },
      },
    },
    {
      "@type": "ListItem",
      position: 2,
      item: {
        "@type": "Service",
        name: "Fenny Prime",
        url: `${SITE_ORIGIN}/prime`,
        serviceType: "Skräddarsydd produktion",
        description: "Engångsproduktioner när ni behöver något extra.",
        offers: { "@type": "Offer", priceCurrency: "SEK", price: "150000" },
      },
    },
    {
      "@type": "ListItem",
      position: 3,
      item: {
        "@type": "Service",
        name: "Fenny Pilot",
        url: TJANSTER_URL,
        serviceType: "Testproduktion",
        description: "En fullskalig engångsproduktion till fast pris.",
        offers: { "@type": "Offer", priceCurrency: "SEK", price: "28995" },
      },
    },
    {
      "@type": "ListItem",
      position: 4,
      item: {
        "@type": "Service",
        name: "Fenny Heritage",
        url: `${SITE_ORIGIN}/heritage`,
        serviceType: "Livsberättelser & kunskapsarv på film",
        description: "Bevarar livsberättelser och tyst kunskap — för privatpersoner och företag.",
      },
    },
    {
      "@type": "ListItem",
      position: 5,
      item: {
        "@type": "Service",
        name: "Fenny Bröllopsfilm",
        url: `${SITE_ORIGIN}/brollopsfilm`,
        serviceType: "Bröllopsfilm",
        description: "Cinematisk bröllopsfilm — 1–3 bröllop per år.",
      },
    },
  ],
};

export const Route = createFileRoute("/tjanster")({
  head: () => ({
    meta: [
      { title: "Tjänster — film för alla behov | Fenny" },
      {
        name: "description",
        content:
          "Fennys tjänster: Pulse (filmabonnemang), Prime (skräddarsydd produktion), Pilot (testproduktion), Heritage (livsberättelser & kunskapsarv) och Bröllopsfilm.",
      },
      { property: "og:title", content: "Tjänster — Fenny" },
      {
        property: "og:description",
        content:
          "Pulse, Prime, Pilot, Heritage och Bröllopsfilm — fem vägar in.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/tjanster" },
      { property: "og:image", content: TJANSTER_OG_IMAGE },
      { property: "og:image:alt", content: "Fenny — tjänster för rörligt innehåll" },
      { property: "og:image:width", content: "1024" },
      { property: "og:image:height", content: "576" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Tjänster — Fenny" },
      { name: "twitter:description", content: "Pulse, Prime, Pilot, Heritage och Bröllop — fem vägar in." },
      { name: "twitter:image", content: TJANSTER_OG_IMAGE },
      { name: "twitter:image:alt", content: "Fenny — tjänster för rörligt innehåll" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/tjanster" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/tjanster", name: "Tjänster — film för alla behov | Fenny", description: "Fennys tjänster: Pulse (filmabonnemang), Prime (skräddarsydd produktion), Pilot (testproduktion), Heritage (livsberättelser & kunskapsarv) och Bröllopsfilm."})) },
      { type: "application/ld+json", children: JSON.stringify(tjansterBreadcrumbJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(tjansterItemListJsonLd) },
    ],
  }),
  component: TjansterPage,
});

const offerings = [
  {
    eyebrow: "Pulse",
    title: "Filmabonnemang för marknadsteam",
    body: "Löpande filmleveranser med fast månadskostnad. Manus, regi, inspelning, klipp, grading, ljudmix och formatanpassning — i en och samma tjänst. Tre nivåer: Lite, Core och Pro.",
    bullets: [
      "Från 14 995 kr/månad (Pulse Lite)",
      "2–6+ klipp per månad beroende på nivå",
      "Format för YouTube, Instagram, LinkedIn, TikTok",
      "Strategisk planering och löpande optimering",
    ],
    cta: { to: "/pulse", label: "Läs om Pulse", track: "how_pulse_works" as const, location: "tjanster_pulse" },
    secondary: { to: "/priser", label: "Se priser" },
    accent: "mint",
    icon: Zap,
    thumb: "https://fenny.se/prime-stills/pricka-fenny-1.jpg",
    thumbAlt: "Intervju med representant för Pricka framför rosa bakgrund i en Fenny-produktion",
  },
  {
    eyebrow: "Prime",
    title: "Skräddarsydda produktioner med stor ambition",
    body: "Engångsproduktioner när ni behöver något extra: brandfilm, reklamfilm, event, lansering. Samma team som i Pulse, samma kvalitetsnivå — men paketerat för det enskilda projektet.",
    bullets: [
      "Skräddarsytt upplägg och budget",
      "Från ca 150 000 kr per produktion",
      "Strategi, manus, produktion, post i ett",
      "Kan kombineras fritt med ett Pulse-abonnemang",
    ],
    cta: { to: "/prime", label: "Läs mer om Prime", track: "how_pulse_works" as const, location: "tjanster_prime" },
    secondary: { to: "/kontakt", label: "Diskutera ert projekt" },
    accent: "peach",
    icon: Sparkles,
    thumb: "https://fenny.se/prime-stills/garageportexperten-fenny-6.jpg",
    thumbAlt: "Stillbild från Fennys produktion för Garageportexperten",
  },
  {
    eyebrow: "Pilot",
    title: "Testa hur vi jobbar — utan att binda upp er",
    body: "En fullskalig engångsproduktion till fast pris. Ni får uppleva hela vår process — från strategi till färdig film — innan ni bestämmer er för ett abonnemang.",
    bullets: [
      "28 995 kr engångskostnad",
      "En klipplängd + en trailer",
      "Samma utrustning och team som i Pulse",
      "Kan endast köpas en gång per kund",
    ],
    cta: { to: "/kontakt", label: "Prata om en pilot", track: "contact_us" as const, location: "tjanster_pilot" },
    secondary: { to: "/priser", label: "Jämför med Pulse" },
    accent: "mint",
    icon: Film,
    thumb: "https://fenny.se/prime-stills/recompany-1.jpg",
    thumbAlt: "Stillbild från Fennys produktion för Recompany",
  },
  {
    eyebrow: "Heritage",
    title: "Livsberättelser och kunskapsarv på film",
    body: "Vår fjärde produkt — film som bevarar det viktigaste. För privatpersoner som vill dokumentera en livsberättelse, och för företag som vill säkra tyst kunskap, grundarberättelser eller företagshistoria innan den försvinner.",
    bullets: [
      "Två spår: privat (livsberättelser) och företag (kunskapsarv)",
      "Heritage Personal från 39 000 kr",
      "Heritage Business från 95 000 kr",
      "Färdig film + arkiverbart råmaterial",
    ],
    cta: { to: "/heritage", label: "Läs om Heritage", track: "how_pulse_works" as const, location: "tjanster_heritage" },
    secondary: { to: "/heritage/livsberattelser", label: "Livsberättelser" },
    accent: "peach",
    icon: Heart,
    thumb: "https://fenny.se/prime-stills/also-fenny-1.jpg",
    thumbAlt: "Person talar inför kameran i en industrilokal under en Fenny-inspelning för ALSO",
  },
  {
    eyebrow: "Bröllop",
    title: "Cinematisk bröllopsfilm — 1–3 bröllop per år",
    body: "Vi tar emot ett mycket begränsat antal bröllop varje år. Samma filmiska hantverk som i våra varumärkesproduktioner — dokumentärt, närvarande och utan poser. För par som vill ha en film som åldras med värdighet.",
    bullets: [
      "Maximalt 1–3 bröllop per år",
      "Dokumentär regi — ingen iscensättning",
      "Highlight-film + längre dokumentär",
      "Råmaterial sparat i 10 år",
    ],
    cta: { to: "/brollopsfilm", label: "Se Bröllopsfilm", track: "how_pulse_works" as const, location: "tjanster_brollop" },
    secondary: { to: "/kontakt", label: "Förfrågan" },
    accent: "peach",
    icon: Camera,
    thumb: "/__l5e/assets-v1/d5c09304-3a91-4c06-bc65-a6c383367747/brollop-30.webp",
    thumbAlt: "Stillbild ur en bröllopsfilm producerad av Fenny",
  },
] as const;

function TjansterPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-40 pb-12 md:pt-48 md:pb-16">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_30%_20%,color-mix(in_oklab,var(--mint)_10%,transparent)_0%,transparent_50%),radial-gradient(ellipse_at_80%_70%,color-mix(in_oklab,var(--peach)_8%,transparent)_0%,transparent_50%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="max-w-3xl">
            <Reveal>
              <LivePulseBadge tone="mint" className="mb-6">
                Tjänster · fem vägar in
              </LivePulseBadge>
            </Reveal>
            <KineticHeading
              text="Film som tjänst — i alla format ni behöver."
              className="text-5xl font-semibold leading-[1.02] text-butter md:text-7xl"
            />
            <Reveal delay={0.7}>
              <p className="mt-7 max-w-2xl text-pretty text-lg text-foreground/85 md:text-xl">
                Fem sätt att jobba med oss, en gemensam kvalitetsnivå: löpande abonnemang (Pulse),
                skräddarsydda produktioner (Prime), en pilot för att testa, samt två specialspår —
                Heritage för livsberättelser och kunskapsarv, och Bröllopsfilm.
              </p>
            </Reveal>
            <Reveal delay={0.9}>
              <div className="mt-9 flex flex-wrap gap-3">
                <Magnetic>
                  <Button to="/kontakt" variant="mint" arrow trackId="book_strategy_meeting" trackProps={{ location: "tjanster_hero" }}>
                    Boka strategimöte
                  </Button>
                </Magnetic>
                <Button href="#matcher" variant="outline">Hitta rätt tjänst</Button>
                <Button href="#jamfor" variant="ghost">Jämför</Button>
                <Button to="/priser" variant="ghost">Se priser</Button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Service Matcher */}
      <Section id="matcher" className="scroll-mt-28 pt-4">
        <Reveal>
          <ServiceMatcher />
        </Reveal>
      </Section>

      {/* Offerings */}
      <Section className="pt-4">
        <div className="space-y-6">
          {offerings.map((o, i) => {
            const Icon = o.icon;
            const accent = o.accent === "peach" ? "text-peach ring-peach/30 bg-peach/15" : "text-mint ring-mint/30 bg-mint/15";
            const glow = o.accent === "peach" ? "peach" : "mint";
            return (
              <Reveal key={o.title}>
                <TiltCard glow={glow as "mint" | "peach"} intensity={3}>
                  <article id={o.eyebrow.toLowerCase()} className="scroll-mt-28 grid gap-8 overflow-hidden rounded-3xl border border-white/10 bg-surface p-8 transition-colors hover:border-mint/30 md:grid-cols-[1.2fr_1fr_1.4fr] md:p-10">
                    <div>
                      <div className={`inline-flex h-12 w-12 items-center justify-center rounded-full ring-1 ${accent}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="mt-5 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        0{i + 1} · {o.eyebrow}
                      </div>
                      <h2 className="mt-3 text-balance text-3xl font-semibold leading-tight md:text-4xl">
                        {o.title}
                      </h2>
                    </div>
                    {/* Thumb */}
                    <div className="relative hidden overflow-hidden rounded-2xl md:block">
                      <img
                        src={o.thumb}
                         alt={o.thumbAlt}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-surface/60 via-transparent to-transparent" />
                    </div>
                    <div>
                      <p className="text-pretty text-base leading-relaxed text-foreground/85 md:text-lg">
                        {o.body}
                      </p>
                      <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                        {o.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2.5 text-sm text-foreground/85">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-7 flex flex-wrap gap-3">
                        <Button
                          to={o.cta.to}
                          variant={o.accent === "peach" ? "peach" : "mint"}
                          arrow
                          trackId={o.cta.track}
                          trackProps={{ location: o.cta.location }}
                        >
                          {o.cta.label}
                        </Button>
                        <Button to={o.secondary.to} variant="outline">
                          {o.secondary.label}
                        </Button>
                      </div>
                    </div>
                  </article>
                </TiltCard>
              </Reveal>
            );
          })}
        </div>

        {/* Specialspår — bröllop tillfälligt dolt */}
      </Section>

      {/* Manus, koncept och planering */}
      <Section className="pt-0">
        <EyebrowHeading
          eyebrow="Innan kameran rullar"
          title="Manus, koncept och planering."
          lede="Vi tar också fristående uppdrag inom manusarbete, konceptutveckling och förproduktion — även när någon annan spelar in filmen."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { to: "/manus-och-koncept", title: "Manus & koncept", body: "Pelaren: strategi, idé och manus från brief till godkänt." },
            { to: "/manusforfattare", title: "Manusförfattare", body: "Manus till reklamfilm, företagsfilm och employer branding." },
            { to: "/reklamfilmsmanus", title: "Reklamfilmsmanus", body: "Hookstruktur och kanalversioner skrivna från start." },
            { to: "/storyboard", title: "Storyboard", body: "Bildmanus som visar filmen innan ni betalar för den." },
            { to: "/konceptutveckling-film", title: "Konceptutveckling", body: "Två till tre idéspår med pris och tidplan per spår." },
            { to: "/forproduktion", title: "Förproduktion", body: "Plan, budget, casting, locations och tillstånd." },
          ].map((c) => (
            <Reveal key={c.to}>
              <Link
                to={c.to}
                className="block h-full rounded-2xl border border-white/10 bg-surface p-6 transition-colors hover:border-mint/40"
              >
                <h3 className="text-lg font-semibold text-butter">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/80">{c.body}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Compare table */}
      <Section id="jamfor" className="scroll-mt-28 bg-surface/30">
        <EyebrowHeading
          eyebrow="Sida vid sida"
          title="Tre tjänster, ett beslut."
          lede="Snabb överblick över hur Pulse, Prime och Pilot skiljer sig — och var de är likadana."
        />
        <div className="mt-12">
          <CompareTable />
        </div>
      </Section>

      <Pillars />

      <FormatShowcase />

      <TrustBand ctaLocation="tjanster_trustband" />

      <ContactForm location="tjanster" />
    </div>
  );
}
